import {
	DateString,
	Icon,
	Space,
	PipeSeparator
} from "client/Components";
import { HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { HubItemMediaPreviewViewModel } from "client/Api/Hub/common";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { generateTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";
import { ListItem } from "../../ListItem";
import { HubItemStatistic } from "../common";
import styles from "./ListItemBase.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubSubtitleWithCategories } from "client/Features/Hub/HubSubtitleWithCategories";

export type ListItemBaseProps = {
	itemKind: HubItemKind;
	id: number;
	title: string;
	media: HubItemMediaPreviewViewModel;
	startDate: string | Date;
	statistics: readonly HubItemStatistic[];
	categoryNames: readonly string[];
	descriptionPreview: string;
	isWideTile: boolean;
	isUltraWideTile: boolean;
	trackingIdBase: string;
	onOpen: (id: number) => void;
	authorName: string | null;
	timezone: TimeZone;
	isNew: boolean;
	isPinned?: boolean;
};

export const ListItemBase = (props: ListItemBaseProps): ReactElement => {
	const {
		itemKind,
		id,
		title,
		media,
		startDate,
		statistics,
		categoryNames,
		descriptionPreview,
		isWideTile,
		trackingIdBase,
		onOpen,
		authorName,
		isUltraWideTile,
		timezone,
		isNew,
		isPinned = false
	} = props;

	const openHandler = useCallback(
		(): void => {
			onOpen(id);
		},
		[onOpen, id]
	);

	return (
		<ListItem
			key={id}
			isHoverable
			trackingId={generateTrackingId(trackingIdBase, `|${id}`)}
			onClick={openHandler}
			isWide={isWideTile}
			title={title}
			hasStatusDot={isNew}
		>
			{isWideTile && (
				<ListItem.Icon>
					<div className={styles.thumbnailContainer}>
						<HubThumbnail
							hubItemKind={itemKind}
							media={media}
							title={title}
							mediaPlaceholderSize="medium"
						/>
					</div>
				</ListItem.Icon>
			)}
			<ListItem.Content>
				<ListItem.Row>
					<ListItem.Title trackingId="title">
						{isPinned && (
							<span className={styles.titleIcon}>
								<Icon
									value={{
										kind: "font",
										name: "thumbtack",
										color: "primary"
									}}
									size="small"
								/>
							</span>
						)}
						{title}
					</ListItem.Title>
				</ListItem.Row>
				<ListItem.Row>
					<ListItem.Info width="fill">
						<HubSubtitleWithCategories
							categoryNames={categoryNames}
						>
							<>
								<DateString
									value={startDate}
									format="RelativeDayWithTime"
									timezone={timezone}
								/>
								{authorName && isUltraWideTile && (
									<>
										<PipeSeparator />
										{localize("_(Edited)By")}
										<Space />
										{authorName}
									</>
								)}
							</>
						</HubSubtitleWithCategories>
					</ListItem.Info>
				</ListItem.Row>
				{isWideTile && (
					<>
						{descriptionPreview && (
							<ListItem.Row>
								<ListItem.Description>
									{descriptionPreview}
								</ListItem.Description>
							</ListItem.Row>
						)}

						<ListItem.Row>
							<div className={styles.statistics}>
								{statistics.map((x, i) => (
									<ListItem.Info key={i}>
										<Icon value={x.icon} size="small" />
										<Space />
										{x.text}
									</ListItem.Info>
								))}
							</div>
						</ListItem.Row>
					</>
				)}
			</ListItem.Content>
		</ListItem>
	);
};
ListItemBase.displayName = "ListItemBase";